/**
 * @file index.tsx
 *
 * @fileoverview Renders multiple strings type field on
 * an entity as a text input.
 */
import React, { useEffect } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import {
  getPlaceholder,
  getSetting,
} from '~/jobiqo/components/Form/helpers/formSettings';
import { EntityField } from '~/jobiqo/models/entity';
import { Button } from '~/vcl/atoms/01-forms/01-button';
import { Input } from '~/vcl/atoms/01-forms/03-input';
import { InlineError } from '~/vcl/atoms/01-forms/09-inline-error';
import { FormItem } from '~/vcl/atoms/01-forms/11-form-item';
import { FormLabel } from '~/vcl/atoms/01-forms/11-form-item/01-form-label';
import { FormDescription } from '~/vcl/atoms/01-forms/11-form-item/02-form-description';
import { FlexItems } from '~/vcl/atoms/06-layout/03-flex';
import { Spacing } from '~/vcl/atoms/06-layout/07-spacing';
import { AddIcon } from '~/vcl/icons/Add';
import { CloseIcon } from '~/vcl/icons/Close';
import { useFieldRules } from '../../hooks/useFieldRules';
import { EntityFieldRules } from '~/jobiqo/models/config';

interface Props extends React.InputHTMLAttributes<HTMLInputElement> {
  /**
   * The field definition.
   */
  entityField: EntityField;
  /**
   * Label to display in each item that is added.
   */
  singleItemLabel?: string;
  /**
   * The field rules to check if the field is required, hidden or disabled.
   */
  fieldRules?: EntityFieldRules;
}

export default function StringMultipleWidget({
  entityField,
  singleItemLabel,
  fieldRules = {},
  ...props
}: Props) {
  const { control, setValue, getValues } = useFormContext();
  const values = getValues();
  const { hidden, disabled, required } = useFieldRules(
    entityField.id,
    values,
    fieldRules,
  );

  const { fields, remove, append, update } = useFieldArray({
    control,
    name: entityField.id,
  });
  const [currentItemToAdd, setCurrentItemToAdd] = React.useState<string>('');

  // When field is hidden reset field state. this needs to be done inside a hook
  // otherwise it will not work.
  useEffect(() => {
    if (hidden && !!values[entityField.id]) {
      setValue(entityField.id, null);
    }
  }, [hidden, values, entityField]);

  if (hidden) {
    return null;
  }

  return (
    <FormItem>
      <FormLabel required={entityField.required}>{entityField.label}</FormLabel>
      <FlexItems gap={3}>
        {fields.map((field, index) => {
          const fieldName = `${entityField.id}.${index}`;
          return (
            <FlexItems key={field.id}>
              <Controller
                name={fieldName}
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <Input
                      {...field}
                      id={field.name}
                      type={entityField.type}
                      required={entityField.required || required}
                      disabled={entityField.disabled || disabled}
                      error={!!error}
                      label={singleItemLabel}
                      size={40}
                      description={entityField.description}
                      placeholder={getPlaceholder(entityField)}
                      maxLength={getSetting('max_length', entityField)}
                      onChange={(e) => {
                        if (entityField.type === 'integer') {
                          update(index, Number.parseInt(e.currentTarget.value));
                        } else {
                          update(index, e.currentTarget.value);
                        }
                      }}
                    />
                    {error && <InlineError message={error.message} />}
                  </>
                )}
              />
              <Button
                raised={false}
                iconed={true}
                buttonStyle="danger"
                type="button"
                onClick={() => remove(index)}
              >
                <CloseIcon title="remove" />
              </Button>
              <Spacing />
            </FlexItems>
          );
        })}
      </FlexItems>
      <Spacing />
      <FlexItems>
        <Controller
          name={entityField.id}
          control={control}
          render={({ field, fieldState: { error } }) => (
            <>
              <Input
                {...field}
                value={currentItemToAdd}
                id={field.name}
                type={entityField.type}
                required={entityField.required}
                disabled={entityField.disabled}
                error={!!error}
                label={singleItemLabel}
                size={40}
                description={entityField.description}
                placeholder={getPlaceholder(entityField)}
                maxLength={getSetting('max_length', entityField)}
                onChange={(e) => {
                  setCurrentItemToAdd(e.currentTarget.value);
                }}
              />
              {entityField.description && (
                <FormDescription content={entityField.description} />
              )}
              {error && (
                <Spacing>
                  <InlineError message={error.message} />
                </Spacing>
              )}
            </>
          )}
        />
        <Button
          raised={false}
          iconed={true}
          buttonStyle="default"
          type="button"
          onClick={() => {
            if (entityField.type === 'integer') {
              append(Number.parseInt(currentItemToAdd));
            } else {
              append(currentItemToAdd);
            }
            setCurrentItemToAdd('');
          }}
        >
          <AddIcon title="add" />
        </Button>
        <Spacing />
      </FlexItems>
    </FormItem>
  );
}

export { StringMultipleWidget };
